import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { endpoints } from "../../api/endpoints";

export const initiateSubscriptionPayment = createAsyncThunk(
  "payment/initiateSubscriptionPayment",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.initiatesubscriptionpayment,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const cancelSubscription = createAsyncThunk(
  "payment/cancel",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.cancelsubscription,
        payload
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
export const updateSubscriptionPlan = createAsyncThunk(
  "payment/updatePlan",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.updatesubscriptionplan,
        payload
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue(error.message);
    }
  }
);

export const confirmSubscriptionPayment = createAsyncThunk(
  "payment/confirmPayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.confirmsubscriptionpayment,
        paymentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const generateAccountSetupLink = createAsyncThunk(
  "payment/generateAccountSetupLink",
  async ({ _id }, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        endpoints.generateaccountsetuplinkrequest,
        { _id }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);
export const checkStripeAccountStatus = createAsyncThunk(
  "payment/checkStripeAccountStatus",
  async ({ _id }, thunkAPI) => {
    try {
      const response = await axiosInstance.post(
        endpoints.checkstripeconnectaccountstatus,
        { _id }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  }
);

export const initiateClientPayment = createAsyncThunk(
  "payment/initiateClientPayment",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.updatepaymentmode,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
export const confirmClientPayment = createAsyncThunk(
  "payment/confirmClientPayment",
  async (paymentData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.updatepaymentinfo,
        paymentData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const deleteStripeConnect = createAsyncThunk(
  "payment/deleteStripeConnect",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        endpoints.deleterecruiterstripeconnect,
        userId
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response.data || "Failed to delete stripe connect"
      );
    }
  }
);

const handlePending = (state) => {
  state.loading = true;
  state.error = null;
};
const handleRejected = (state, action) => {
  state.loading = false;
  state.error = action.payload || "Something went wrong";
};
const handleSubscriptionFulfilled = (state, action) => {
  state.loading = false;
  state.data = action.payload;
  state.clientSecretKey = action.payload.data.subscription_clientsecret;
  state.Publishablekey = action.payload.data.stripe_PublicKey;
  state.subscription_stripepaymentid =
    action.payload.data.subscription_stripepaymentid;
};
const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    subscriptionData: null,
    stripeAccountStatus: null,
    data: null,
    clientSecretKey: null,
    Publishablekey: null,
    subscription_stripepaymentid: null,
    accountSetupLink: null,
    loading: false,
    error: null,
  },
  reducers: {
    setClientSecretKey: (state, action) => {
      state.clientSecretKey = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateSubscriptionPayment.pending, handlePending)
      .addCase(
        initiateSubscriptionPayment.fulfilled,
        handleSubscriptionFulfilled
      )
      .addCase(initiateSubscriptionPayment.rejected, handleRejected)
      .addCase(updateSubscriptionPlan.pending, handlePending)
      .addCase(updateSubscriptionPlan.fulfilled, handleSubscriptionFulfilled)
      .addCase(updateSubscriptionPlan.rejected, handleRejected)
      .addCase(confirmSubscriptionPayment.pending, handlePending)
      .addCase(confirmSubscriptionPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionData = action.payload;
      })
      .addCase(confirmSubscriptionPayment.rejected, handleRejected)
      .addCase(generateAccountSetupLink.pending, handlePending)
      .addCase(generateAccountSetupLink.fulfilled, (state, action) => {
        state.loading = false;
        state.accountSetupLink = action.payload;
      })
      .addCase(generateAccountSetupLink.rejected, handleRejected)
      .addCase(checkStripeAccountStatus.pending, handlePending)
      .addCase(checkStripeAccountStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.stripeAccountStatus = action.payload;
      })
      .addCase(checkStripeAccountStatus.rejected, handleRejected)
      .addCase(initiateClientPayment.pending, handlePending)
      .addCase(initiateClientPayment.fulfilled, handleSubscriptionFulfilled)
      .addCase(initiateClientPayment.rejected, handleRejected)
      .addCase(confirmClientPayment.pending, handlePending)
      .addCase(confirmClientPayment.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptionData = action.payload;
      })
      .addCase(confirmClientPayment.rejected, handleRejected);
  },
});

export const { setClientSecretKey } = paymentSlice.actions;
export default paymentSlice.reducer;
